(() => {
  const SELECTOR = '.amr-reels';
  const MARQUEE_AUTOPLAY_ALL = true; // autoplay all videos at once (heaviest)

  function clamp(n, min, max) {
    return Math.max(min, Math.min(max, n));
  }

  function parseBool(v) {
    return String(v) === 'true';
  }

  function getYouTubeId(url) {
    if (!url) return null;
    const str = String(url).trim();

    // If user pasted only an ID (optionally with query params), accept it.
    const raw = str.replace(/^@/, '');
    const rawBeforeQuery = raw.split('?')[0].split('&')[0].split('#')[0];
    // YouTube video IDs are 11 chars (including Shorts).
    if (!rawBeforeQuery.includes('/') && /^[a-zA-Z0-9_-]{11}$/.test(rawBeforeQuery)) {
      return rawBeforeQuery;
    }
    // Common formats:
    // https://www.youtube.com/watch?v=ID
    // https://youtu.be/ID
    // https://www.youtube.com/shorts/ID
    // https://www.youtube.com/embed/ID
    const patterns = [
      /[?&]v=([^&]+)/,
      /youtu\.be\/([^?&#/]+)/,
      /youtube\.com\/shorts\/([^?&#/]+)/,
      /youtube\.com\/embed\/([^?&#/]+)/,
    ];
    for (const re of patterns) {
      const m = str.match(re);
      if (m && m[1] && /^[a-zA-Z0-9_-]{11}$/.test(m[1])) return m[1];
    }
    return null;
  }

  function buildAutoplayIframe(id) {
    const iframe = document.createElement('iframe');
    iframe.className = 'amr-reels__iframe';
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
    );
    iframe.setAttribute('allowfullscreen', 'allowfullscreen');
    iframe.setAttribute('title', 'YouTube video');
    iframe.setAttribute('loading', 'eager');

    // Most reliable autoplay combo: muted + playsinline.
    // Keep loop (playlist=id) but avoid extra API dependencies.
    const qp = new URLSearchParams({
      autoplay: '1',
      mute: '1',
      playsinline: '1',
      controls: '0',
      disablekb: '1',
      fs: '0',
      iv_load_policy: '3',
      cc_load_policy: '0',
      rel: '0',
      modestbranding: '1',
      loop: '1',
      playlist: id,
    });
    iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(id)}?${qp.toString()}`;
    return iframe;
  }

  // When autoplaying all videos, we don't prune.
  function pruneIframes() {}

  function destroyAnyPlayersInSection(section) {
    if (!section) return;
    section.querySelectorAll('[data-amr-reels-player]').forEach((el) => el.remove());
    section.querySelectorAll('.amr-reels__iframe').forEach((el) => el.remove());
    section.querySelectorAll('.amr-reels__slide').forEach((s) => s.removeAttribute('data-amr-playing'));
    delete section.__amrActiveVideoId;
    delete section.__amrActiveSlide;
  }

  function mountPlayerForSlide(slide) {
    const media = slide.querySelector('[data-amr-reels-media]');
    if (!media) return;

    const idFromLiquid = media.getAttribute('data-video-id') || media.dataset.videoId;
    const url = media.getAttribute('data-video-url') || media.dataset.videoUrl;
    const id = idFromLiquid || getYouTubeId(url);
    if (!id) return;

    // Already mounted for this slide
    if (media.querySelector('[data-amr-reels-player]')) {
      slide.setAttribute('data-amr-playing', 'true');
      return;
    }

    // Restore all posters first, then hide this one.
    const section = slide.closest(SELECTOR);
    if (section) {
      // keep other posters unchanged; only hide this slide's poster
    }

    const poster = slide.querySelector('[data-amr-reels-poster]');

    if (!section) return;

    // Placeholder wrapper (helps CSS target injected content).
    const holder = document.createElement('div');
    holder.setAttribute('data-amr-reels-player', 'true');
    holder.id = `amr-yt-${section.getAttribute('data-section-id') || 's'}-${Date.now()}`;
    media.appendChild(holder);

    const iframe = buildAutoplayIframe(id);
    // Hide the iframe until it loads to avoid showing YouTube UI overlays during load.
    iframe.style.opacity = '0';
    iframe.style.transition = 'opacity 180ms ease';
    iframe.addEventListener(
      'load',
      () => {
        // Once loaded, reveal iframe and hide poster.
        iframe.style.opacity = '1';
        if (poster) poster.style.display = 'none';
      },
      { once: true }
    );
    holder.appendChild(iframe);

    slide.setAttribute('data-amr-playing', 'true');
  }

  function setSlideBasis(section, perView) {
    const track = section.querySelector('[data-amr-reels-track]');
    const slides = track ? Array.from(track.children) : [];
    if (!slides.length) return;

    const gap = 14; // matches CSS
    const viewport = section.querySelector('[data-amr-reels-viewport]');
    const viewportWidth = viewport ? viewport.clientWidth : 0;
    if (!viewportWidth) return;

    const slideWidth = Math.floor((viewportWidth - gap * (perView - 1)) / perView);
    slides.forEach((s) => {
      s.style.width = `${slideWidth}px`;
    });
  }

  function createDots(dotsEl, pageCount, onSelect) {
    dotsEl.innerHTML = '';
    const dots = [];
    for (let i = 0; i < pageCount; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'amr-reels__dot';
      b.setAttribute('aria-label', `Go to slide group ${i + 1}`);
      b.addEventListener('click', () => onSelect(i));
      dotsEl.appendChild(b);
      dots.push(b);
    }
    return dots;
  }

  function pickMostCenteredSlide(section, slides) {
    const viewport = section.querySelector('[data-amr-reels-viewport]');
    if (!viewport) return null;
    const vp = viewport.getBoundingClientRect();
    const vpCenter = vp.left + vp.width / 2;
    let best = null;
    let bestDist = Infinity;
    for (const s of slides) {
      const r = s.getBoundingClientRect();
      const c = r.left + r.width / 2;
      const dist = Math.abs(c - vpCenter);
      if (dist < bestDist) {
        bestDist = dist;
        best = s;
      }
    }
    return best;
  }

  function initMarquee(section) {
    const viewport = section.querySelector('[data-amr-reels-viewport]');
    const track = section.querySelector('[data-amr-reels-track]');
    const prev = section.querySelector('.amr-reels__nav--prev');
    const next = section.querySelector('.amr-reels__nav--next');
    const dotsEl = section.querySelector('[data-amr-reels-dots]');
    if (!viewport || !track) return;

    section.classList.add('is-marquee');

    // Marquee is smooth continuous; hide manual UI.
    if (prev) prev.style.display = 'none';
    if (next) next.style.display = 'none';
    if (dotsEl) dotsEl.style.display = 'none';

    // Marquee uses transform-based motion; keep viewport clipped.
    viewport.style.overflowX = 'hidden';

    // IMPORTANT: keep ONLY the original slides (8 cards).
    const originalSlides = Array.from(track.children);
    if (originalSlides.length < 2) {
      if (originalSlides[0]) mountPlayerForSlide(originalSlides[0]);
      return;
    }

    let paused = false;
    let rafId = null;
    let lastTs = 0;
    const pxPerSec = 28; // slow smooth ticker speed
    let offsetX = 0;

    function step(ts) {
      if (!lastTs) lastTs = ts;
      const dt = Math.min(64, ts - lastTs);
      lastTs = ts;

      if (!paused) {
        offsetX -= (pxPerSec * dt) / 1000;
        track.style.transform = `translate3d(${offsetX}px, 0, 0)`;

        // If the first slide has fully moved out, rotate it to the end.
        const first = track.firstElementChild;
        if (first && first instanceof HTMLElement) {
          const firstWidth = first.getBoundingClientRect().width;
          const gap = parseFloat(getComputedStyle(track).gap) || 14;
          if (-offsetX >= firstWidth + gap) {
            track.appendChild(first);
            offsetX += firstWidth + gap;
            track.style.transform = `translate3d(${offsetX}px, 0, 0)`;
          }
        }
      }

      rafId = window.requestAnimationFrame(step);
    }

    function start() {
      if (rafId) return;
      lastTs = 0;
      rafId = window.requestAnimationFrame(step);
    }

    function stop() {
      if (!rafId) return;
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }

    // Pause when hovering any card (more intuitive than whole section).
    track.addEventListener('mouseover', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest('.amr-reels__card')) paused = true;
    });
    track.addEventListener('mouseout', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      // Resume only when leaving the card area entirely.
      const related = e.relatedTarget;
      if (related instanceof Element && related.closest('.amr-reels__card')) return;
      if (t.closest('.amr-reels__card')) paused = false;
    });
    viewport.addEventListener('touchstart', () => {
      paused = true;
    }, { passive: true });
    viewport.addEventListener('touchend', () => {
      paused = false;
    }, { passive: true });

    // Allow click-to-play as well (works in storefront preview).
    track.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const slide = target.closest('.amr-reels__slide');
      if (!slide) return;
      mountPlayerForSlide(slide);
      paused = true;
      window.setTimeout(() => (paused = false), 2500);
    });

    // Autoplay ALL 8 fixed cards once (no swapping/pruning).
    originalSlides.forEach((s) => mountPlayerForSlide(s));
    start();

    // If section gets removed (theme editor), stop animation.
    const mo = new MutationObserver(() => {
      if (!document.body.contains(section)) {
        stop();
        mo.disconnect();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function initFallback(section) {
    const viewport = section.querySelector('[data-amr-reels-viewport]');
    const track = section.querySelector('[data-amr-reels-track]');
    const prev = section.querySelector('.amr-reels__nav--prev');
    const next = section.querySelector('.amr-reels__nav--next');
    const dotsEl = section.querySelector('[data-amr-reels-dots]');
    if (!viewport || !track) return;

    const autoplay = parseBool(section.getAttribute('data-autoplay'));
    const interval = clamp(parseInt(section.getAttribute('data-autoplay-interval') || '2500', 10) || 2500, 800, 20000);
    const speed = clamp(parseInt(section.getAttribute('data-speed') || '350', 10) || 350, 100, 2000);

    const perViewDesktop = parseInt(section.getAttribute('data-per-view-desktop') || '5', 10) || 5;
    const perViewTablet = parseInt(section.getAttribute('data-per-view-tablet') || '3', 10) || 3;
    const perViewMobile = parseInt(section.getAttribute('data-per-view-mobile') || '1', 10) || 1;

    const slides = Array.from(track.children);
    if (!slides.length) return;

    let timer = null;
    let activePage = 0;
    let dots = [];

    function getPerView() {
      const w = window.innerWidth || 1200;
      if (w <= 767) return perViewMobile;
      if (w <= 991) return perViewTablet;
      return perViewDesktop;
    }

    function getPageCount(perView) {
      return Math.max(1, Math.ceil(slides.length / perView));
    }

    function scrollToPage(pageIdx, behavior = 'smooth') {
      const perView = getPerView();
      const pageCount = getPageCount(perView);
      activePage = (pageIdx + pageCount) % pageCount;

      // Stop any playing players when changing page.
      destroyAnyPlayersInSection(section);
      section.querySelectorAll('[data-amr-reels-poster]').forEach((el) => (el.style.display = 'block'));

      const idx = activePage * perView;
      const target = slides[idx] ? slides[idx].offsetLeft : 0;
      viewport.scrollTo({ left: target, behavior });

      dots.forEach((d, i) => d.classList.toggle('is-active', i === activePage));

      // Autoplay only the first slide of the visible group (avoid blank posters).
      if (slides[idx]) mountPlayerForSlide(slides[idx]);
    }

    function rebuild() {
      const perView = getPerView();
      setSlideBasis(section, perView);
      const pageCount = getPageCount(perView);
      dots = dotsEl ? createDots(dotsEl, pageCount, (i) => scrollToPage(i)) : [];
      scrollToPage(activePage, 'auto');
    }

    function start() {
      if (!autoplay) return;
      stop();
      timer = window.setInterval(() => scrollToPage(activePage + 1), interval);
    }

    function stop() {
      if (timer) window.clearInterval(timer);
      timer = null;
    }

    if (prev) prev.addEventListener('click', () => scrollToPage(activePage - 1));
    if (next) next.addEventListener('click', () => scrollToPage(activePage + 1));

    // Click any card to play it.
    track.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const slide = target.closest('.amr-reels__slide');
      if (!slide) return;
      mountPlayerForSlide(slide);
      stop();
    });

    section.addEventListener('mouseenter', stop);
    section.addEventListener('mouseleave', start);
    viewport.addEventListener('touchstart', stop, { passive: true });
    viewport.addEventListener('touchend', start, { passive: true });

    // Smoothness for manual scroll nudges
    viewport.style.scrollBehavior = 'smooth';
    track.style.transition = `transform ${speed}ms ease`;

    rebuild();
    start();

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(rebuild, 150);
    });
  }

  function initCenter(section) {
    const track = section.querySelector('[data-amr-reels-track]');
    const prev = section.querySelector('.amr-reels__nav--prev');
    const next = section.querySelector('.amr-reels__nav--next');
    const dotsEl = section.querySelector('[data-amr-reels-dots]');
    if (!track) return;

    section.classList.add('is-center');
    if (prev) prev.style.display = 'none';
    if (next) next.style.display = 'none';
    if (dotsEl) dotsEl.style.display = 'none';

    track.style.transform = 'none';
    Array.from(track.children).forEach((slide) => mountPlayerForSlide(slide));

    track.addEventListener('click', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const slide = target.closest('.amr-reels__slide');
      if (!slide) return;
      mountPlayerForSlide(slide);
    });
  }

  function initSection(section) {
    const motion = (section.getAttribute('data-motion') || '').toLowerCase();
    if (motion === 'marquee') {
      initMarquee(section);
      return;
    }
    if (motion === 'center') {
      initCenter(section);
      return;
    }

    initFallback(section);
  }

  function boot() {
    document.querySelectorAll(SELECTOR).forEach((section) => initSection(section));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();

